// Encapsulates save, rename, and delete dialogs for user-owned screener views.
import { type FormEvent, useState } from "react";
import {
  MAX_SAVED_VIEW_NAME_LENGTH,
  normalizeSavedViewName,
  type SavedViewRecord
} from "../../lib/saved-views-api";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

interface SavedViewActionsProps {
  selectedSavedView: SavedViewRecord | null;
  authenticated: boolean;
  accountAvailability: "loading" | "ready" | "error";
  mutationPending: boolean;
  onCreate: (name: string) => void;
  onRename: (view: SavedViewRecord, name: string) => void;
  onDelete: (view: SavedViewRecord) => void;
}

/** Provides lower-frequency saved-view mutations while preserving existing gating rules. */
export function SavedViewActions({
  selectedSavedView,
  authenticated,
  accountAvailability,
  mutationPending,
  onCreate,
  onRename,
  onDelete
}: SavedViewActionsProps) {
  const [saveOpen, setSaveOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [newViewName, setNewViewName] = useState("");
  const [renameViewName, setRenameViewName] = useState("");
  const canPersist = authenticated && accountAvailability === "ready" && !mutationPending;
  const canMutateSelected = canPersist && selectedSavedView !== null;

  const submitNewView = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = normalizeSavedViewName(newViewName);
    if (!canPersist || name === null) return;
    onCreate(name);
    setSaveOpen(false);
    setNewViewName("");
  };

  const submitRename = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = normalizeSavedViewName(renameViewName);
    if (!canMutateSelected || name === null || selectedSavedView === null) return;
    onRename(selectedSavedView, name);
    setRenameOpen(false);
  };

  const confirmDelete = () => {
    if (!canMutateSelected || selectedSavedView === null) return;
    onDelete(selectedSavedView);
    setDeleteOpen(false);
  };

  return (
    <div className="flex flex-wrap gap-1.5">
      <Popover open={saveOpen} onOpenChange={setSaveOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" size="sm" disabled={!canPersist}>
            Save view
          </Button>
        </PopoverTrigger>
        <PopoverContent role="dialog" aria-label="Save view" align="end" className="w-72">
          <form className="space-y-3" onSubmit={submitNewView}>
            <h3 className="text-sm font-semibold">Save current view</h3>
            <label className="block space-y-1 text-xs font-medium">
              <span>View name</span>
              <input
                autoFocus
                required
                type="text"
                maxLength={MAX_SAVED_VIEW_NAME_LENGTH}
                value={newViewName}
                onChange={(event) => setNewViewName(event.target.value)}
                className="h-8 w-full rounded-md border border-input bg-surface-elevated px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              />
            </label>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setSaveOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                size="sm"
                disabled={!canPersist || normalizeSavedViewName(newViewName) === null}
              >
                Save new view
              </Button>
            </div>
          </form>
        </PopoverContent>
      </Popover>

      <Popover
        open={renameOpen}
        onOpenChange={(open) => {
          if (open && selectedSavedView) setRenameViewName(selectedSavedView.name);
          setRenameOpen(open);
        }}
      >
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" size="sm" disabled={!canMutateSelected}>
            Rename view
          </Button>
        </PopoverTrigger>
        <PopoverContent role="dialog" aria-label="Rename view" align="end" className="w-72">
          <form className="space-y-3" onSubmit={submitRename}>
            <h3 className="text-sm font-semibold">Rename saved view</h3>
            <label className="block space-y-1 text-xs font-medium">
              <span>View name</span>
              <input
                autoFocus
                required
                type="text"
                maxLength={MAX_SAVED_VIEW_NAME_LENGTH}
                value={renameViewName}
                onChange={(event) => setRenameViewName(event.target.value)}
                className="h-8 w-full rounded-md border border-input bg-surface-elevated px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              />
            </label>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setRenameOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                size="sm"
                disabled={!canMutateSelected || normalizeSavedViewName(renameViewName) === null}
              >
                Rename saved view
              </Button>
            </div>
          </form>
        </PopoverContent>
      </Popover>

      <Popover open={deleteOpen} onOpenChange={setDeleteOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" size="sm" disabled={!canMutateSelected}>
            Delete view
          </Button>
        </PopoverTrigger>
        <PopoverContent role="dialog" aria-label="Delete view" align="end" className="w-72">
          <div className="space-y-3">
            <h3 className="text-sm font-semibold">Delete saved view?</h3>
            <p className="text-xs text-muted-foreground">
              This removes {selectedSavedView?.name ?? "this saved view"} from your account.
            </p>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setDeleteOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="default"
                size="sm"
                disabled={!canMutateSelected}
                onClick={confirmDelete}
              >
                Delete saved view
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
